/**
 * JsonViewer - collapsible, syntax-highlighted rendering of arbitrary JSON data.
 * Replaces flat `JSON.stringify(x, null, 2)` dumps in execution result/input panels.
 */

import React, { useState } from 'react';
import './JsonViewer.css';

interface JsonViewerProps {
  data: unknown;
}

const isCollapsible = (value: unknown): value is Record<string, unknown> | unknown[] =>
  value !== null && typeof value === 'object';

const Primitive: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null) return <span className="jv-null">null</span>;
  if (typeof value === 'string') return <span className="jv-string">"{value}"</span>;
  if (typeof value === 'number') return <span className="jv-number">{value}</span>;
  if (typeof value === 'boolean') return <span className="jv-boolean">{String(value)}</span>;
  return <span className="jv-string">{String(value)}</span>;
};

// Deeply nested payloads (e.g. a raw LLM response echoed back as a field) start
// collapsed past this depth so the panel opens as a skimmable outline rather than
// one giant wall of text; each node can still be expanded individually on click.
const AUTO_COLLAPSE_DEPTH = 3;

const JsonNode: React.FC<{ name?: string; value: unknown; depth: number; isLast: boolean }> = ({
  name,
  value,
  depth,
  isLast,
}) => {
  const collapsible = isCollapsible(value);
  const [collapsed, setCollapsed] = useState(collapsible && depth >= AUTO_COLLAPSE_DEPTH);

  const keyLabel = name !== undefined ? (
    <>
      <span className="jv-key">"{name}"</span>
      <span className="jv-punct">: </span>
    </>
  ) : null;

  if (!collapsible) {
    return (
      <div className="jv-line" style={{ paddingLeft: depth * 16 }}>
        {keyLabel}
        <Primitive value={value} />
        {!isLast && <span className="jv-punct">,</span>}
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const entries = isArray
    ? (value as unknown[]).map((v, i) => [i, v] as const)
    : Object.entries(value as Record<string, unknown>);
  const openBracket = isArray ? '[' : '{';
  const closeBracket = isArray ? ']' : '}';
  const count = entries.length;

  return (
    <div className="jv-node">
      <div
        className="jv-line jv-collapsible"
        style={{ paddingLeft: depth * 16 }}
        onClick={() => setCollapsed(c => !c)}
      >
        <span className="jv-caret">{collapsed ? '▶' : '▼'}</span>
        {keyLabel}
        <span className="jv-punct">{openBracket}</span>
        {collapsed ? (
          <>
            <span className="jv-collapsed-summary">
              {count} {isArray ? (count === 1 ? 'item' : 'items') : (count === 1 ? 'key' : 'keys')}
            </span>
            <span className="jv-punct">{closeBracket}</span>
            {!isLast && <span className="jv-punct">,</span>}
          </>
        ) : count === 0 ? (
          <span className="jv-punct">{closeBracket}</span>
        ) : null}
      </div>
      {!collapsed && count > 0 && (
        <>
          {entries.map(([k, v], i) => (
            <JsonNode
              key={isArray ? i : (k as string)}
              name={isArray ? undefined : (k as string)}
              value={v}
              depth={depth + 1}
              isLast={i === entries.length - 1}
            />
          ))}
          <div className="jv-line" style={{ paddingLeft: depth * 16 }}>
            <span className="jv-punct">{closeBracket}</span>
            {!isLast && <span className="jv-punct">,</span>}
          </div>
        </>
      )}
    </div>
  );
};

export const JsonViewer: React.FC<JsonViewerProps> = ({ data }) => {
  if (data === undefined) return null;
  return (
    <div className="json-viewer-tree">
      <JsonNode value={data} depth={0} isLast />
    </div>
  );
};
